import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const workSteps = [
  {
    icon: 'work-icon-01.svg',
    title: 'Step 1 - Use Case Selection and Risk Framing',
    desc: 'We identify where GenAI creates measurable value, define success metrics, establish safety boundaries, and map how outputs will be reviewed or acted upon.',
  },
  {
    icon: 'work-icon-02.svg',
    title: 'Step 2 - Knowledge and Data Readiness',
    desc: 'We prepare your content and knowledge sources for retrieval, define access rules, reduce duplication, and set up tenant-safe permissioning for answers.',
  },
  {
    icon: 'work-icon-03.svg',
    title: 'Step 3 - RAG, Prompting, and Guardrail Design',
    desc: 'We implement retrieval-augmented generation, prompt versioning, structured outputs, and safety controls such as injection defenses and policy filters.',
  },
  {
    icon: 'work-icon-04.svg',
    title: 'Step 4 - Production Integration and Evaluation',
    desc: 'We integrate GenAI into your product or workflows, build test sets and eval harnesses, measure quality and latency, and ship with staged rollout controls.',
  },
  {
    icon: 'work-icon-05.svg',
    title: 'Step 5 - Monitoring, Optimization, and Continuous Improvement',
    desc: 'We monitor quality, drift, cost, and failure modes, improve retrieval and prompts, and establish governance for safe iteration over time.',
  },
];

const faqItems = [
  {
    question: 'What GenAI use cases deliver value reliably in production?',
    answer:
      'The most dependable use cases are those with clear boundaries and measurable outcomes. Examples include support drafting, knowledge base Q&A with citations, document summarization, intake and triage, structured extraction from text, internal copilots for policies and procedures, and content assistance for marketing and product teams. We prioritize scenarios where outputs can be validated and where incorrect responses have controlled impact.',
  },
  {
    question: 'Do you build chatbots, copilots, or both?',
    answer:
      'Both. A chatbot is typically conversational and user-facing, while a copilot is workflow-embedded and helps users complete tasks such as writing responses, generating summaries, or filling structured forms. Copilots tend to deliver stronger ROI because they connect directly to a business process and can be measured through throughput, quality, and cycle time improvements.',
  },
  {
    question: 'How do you reduce hallucinations and incorrect answers?',
    answer:
      'We reduce errors using retrieval-augmented generation, tight prompting, structured outputs, and validation. We also implement safeguards like confidence signals, citations to sources, refusal behavior for unknowns, and human review paths for high-impact scenarios. When the model should not answer, the system should say so clearly and route the user to a reliable next step.',
  },
  {
    question: 'How do you prevent data leakage and ensure permissions are respected?',
    answer:
      'We design retrieval with access control. Content is segmented by tenant, role, and entitlement so users can only retrieve what they are allowed to see. We minimize prompt data, redact sensitive fields, and avoid logging sensitive content. For enterprise environments, we also provide audit logs for access and model actions where required.',
  },
  {
    question: 'Can GenAI trigger actions like creating tickets or updating records?',
    answer:
      'Yes, with the right controls. We use tool calling with strict allowlists, structured schemas, and validation before execution. For sensitive actions, we add approvals and confirmation steps. The goal is to keep the model assisting, while deterministic systems enforce rules and prevent unintended actions.',
  },
  {
    question: 'How do you evaluate GenAI quality before and after launch?',
    answer:
      'We build an evaluation set based on your real queries and edge cases, then measure answer quality, grounding, refusal correctness, and safety behavior. After launch, we monitor quality signals such as user feedback, correction rates, escalation rates, and retrieval coverage. We also track latency and cost per interaction to keep the system sustainable.',
  },
  {
    question: 'What does the typical timeline look like for a GenAI feature?',
    answer:
      'Many teams can reach a controlled pilot in 3 to 6 weeks for a focused use case, depending on knowledge readiness and integration complexity. Production rollout typically follows in phases, with evaluation, monitoring, and governance in place before scaling to more users or more workflows.',
  },
];

const ServiceGenerativeAI = () => {
  useEffect(() => {
    if (window.WOW) new window.WOW().init();
    if (window.initMagnetic) window.initMagnetic();
  }, []);

  return (
    <>
      <section className="breadcrumbs">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-wrapper">
                <div className="breadcrumb-cnt">
                  <span>Services Details</span>
                  <h1>Generative AI Solutions</h1>
                  <div className="breadcrumb-list">
                    <Link to="/">Home</Link>
                    <img src="/assets/img/content/breadcrumb-arrow.svg" alt="" />
                    Services Details
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="service-details">
        <div className="about-services sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 d-flex align-items-center wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="about-content">
                  <div className="section-title-5">
                    <h2>
                      Generative AI <br /> Solutions
                    </h2>
                  </div>

                  <p>
                    We deliver production-grade generative AI features that help teams work faster and make platforms more useful. This includes copilots, assistants, document automation, knowledge base Q&A, and content drafting systems that are grounded in your data and designed with safety controls.
                  </p>

                  <p>
                    Successful GenAI is not just a model connected to a prompt. It is a system with retrieval, access control, evaluation, monitoring, and guardrails. We build GenAI capabilities that are measurable, cost-aware, and safe to operate in real environments where content changes and edge cases are common.
                  </p>

                  <ul className="about-featue">
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Copilots and assistants embedded in workflows
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Retrieval-augmented generation for grounded answers
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Document summarization, drafting, and structured extraction
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Guardrails: prompt injection defenses and policy filters
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Evaluation harnesses, monitoring, and quality iteration
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Cost and latency controls for sustainable usage
                    </li>
                  </ul>
                </div>
              </div>

              <div
                className="col-lg-6 wow animate fadeInRight"
                data-wow-delay="300ms"
                data-wow-duration="1500ms"
              >
                <div className="service-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/laptop.png" alt="Service" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="how-we-work-section sec-mar">
          <div className="container">
            <div
              className="row mb-60 wow animate fadeInUp"
              data-wow-delay="200ms"
              data-wow-duration="1500ms"
            >
              <div className="col-lg-12">
                <div className="section-title">
                  <h2>How We Work</h2>
                </div>
              </div>
            </div>
          </div>

          <div className="work-item">
            <div className="container-fluid">
              <div className="row row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 row-cols-1 gy-5 justify-content-center">
                {workSteps.map((step, idx) => (
                  <div
                    key={step.title}
                    className="col magnetic-item wow animate fadeInUp"
                    data-wow-delay={`${200 + idx * 100}ms`}
                    data-wow-duration="1500ms"
                  >
                    <div className="single-work">
                      <div className="work-icon">
                        <img src={`/assets/img/content/${step.icon}`} alt="" />
                      </div>
                      <div className="work-content">
                        <h3>{step.title}</h3>
                        <p>{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="service-sort-driscription-area sec-mar">
          <div className="container">
            <div className="row">
              <div
                className="col-lg-12 wow animate fadeInUp"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="driscription-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/rectangular_image.png" alt="Description" />
                </div>
              </div>

              <div className="driscription-content">
                <div className="row">
                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="200ms"
                    data-wow-duration="1500ms"
                  >
                    <p>
                      Production GenAI requires strong grounding and predictable behavior. We typically use retrieval-augmented generation so answers are based on your approved knowledge sources. We structure the retrieval layer to return the right passages, reduce duplication, and improve recall. For reliability, we use response schemas, validation rules, and refusal behavior when the system does not have enough evidence to answer.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Knowledge preparation: chunking, metadata, and source governance
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Tenant-safe retrieval with role and entitlement filtering
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Structured outputs for safe downstream automation
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Safety controls for prompt injection and unsafe content
                      </li>
                    </ul>

                    <p>
                      We also make cost and latency part of the design. That includes caching, response length controls, retrieval narrowing, model selection by task, and rate limits. The goal is a feature that scales financially as usage grows.
                    </p>
                  </div>

                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="300ms"
                    data-wow-duration="1500ms"
                  >
                    <p>
                      GenAI adoption improves when the system is embedded into real workflows and accountability is clear. We integrate assistants into your product UI, support tools, internal portals, or content systems so users can act on the output immediately. We also implement feedback capture so the system learns from corrections and the organization can measure value and risk.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        SaaS and customer support: draft replies, summarize tickets, suggest next steps
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Sales teams: proposal drafts, call summaries, account research from internal sources
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Legal and compliance: policy Q&A with citations and controlled access workflows
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Finance operations: summarize invoices, extract fields, explain variances with evidence
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Product and marketing: content drafting with brand guidelines and reuse across channels
                      </li>
                    </ul>

                    <p>
                      Typical deliverables include a grounded assistant or copilot, a curated knowledge layer with access controls, evaluation results and test sets, and operational dashboards tracking quality, latency, usage, and cost.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="service-faq-area sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="faq-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/faqs.png" alt="FAQ" />
                </div>
              </div>

              <div
                className="col-lg-6 d-flex align-items-center wow animate fadeInRight"
                data-wow-delay="300ms"
                data-wow-duration="1500ms"
              >
                <div className="faq-content-area">
                  <h2>Frequently Asked Questions</h2>

                  <div className="accordion" id="accordionExample">
                    {faqItems.map((item, index) => (
                      <div key={item.question} className="accordion-item">
                        <h6 className="accordion-header" id={`heading${index}`}>
                          <button
                            className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapse${index}`}
                            aria-expanded={index === 0 ? 'true' : 'false'}
                            aria-controls={`collapse${index}`}
                          >
                            {item.question}
                          </button>
                        </h6>

                        <div
                          id={`collapse${index}`}
                          className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                          aria-labelledby={`heading${index}`}
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">{item.answer}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceGenerativeAI;



